import { useState, useRef, useCallback } from "react";

import { Badge } from "@bigbinary/neeto-atoms";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@bigbinary/neeto-atoms/primitives";
import classNames from "classnames";
import { CircleCheck } from "lucide-react";
import { globalProps } from "neetocommons/initializers";
import { useTranslation } from "react-i18next";

import { useFetchLoggedInOrganizations } from "./hooks/reactQuery/useFetchLoggedInOrganizations.js";
import MenuItemButton from "./MenuButton";

const OrganizationSwitcher = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const { data: { organizations, authAppUrl } = {} } =
    useFetchLoggedInOrganizations(globalProps?.user?.email);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const isCurrentSubdomain = link => {
    try {
      const currentSubdomain = window.location.hostname.split(".")[0];
      const linkSubdomain = new URL(link).hostname.split(".")[0];

      return currentSubdomain === linkSubdomain;
    } catch {
      return false;
    }
  };

  return (
    <Popover {...{ open }} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <MenuItemButton
          onClick={() => setOpen(prev => !prev)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </PopoverAnchor>
      <PopoverContent
        align="center"
        className="z-[99999] w-56 gap-0.5 p-1"
        side="right"
        sideOffset={8}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {organizations?.map(organization => (
          <a
            href={organization.organizationUrl}
            key={organization.userId}
            rel="noreferrer"
            target="_blank"
            className={classNames(
              "hover:bg-accent flex h-9 w-full items-center gap-2 rounded-md px-2.5 text-sm font-medium",
              {
                "text-green-600": isCurrentSubdomain(
                  organization.organizationUrl
                ),
              }
            )}
          >
            {organization.orgName}
            {isCurrentSubdomain(organization.organizationUrl) && (
              <Badge variant="default">
                <CircleCheck size={12} />
                {t("neetoMolecules.sidebar.organizationSwitcher.signedin")}
              </Badge>
            )}
          </a>
        ))}
        <a
          className="hover:bg-accent flex h-9 w-full items-center gap-2 rounded-md px-2.5 text-sm font-medium"
          href={authAppUrl}
          rel="noreferrer"
          target="_blank"
        >
          {t("neetoMolecules.sidebar.organizationSwitcher.addAccount")}
        </a>
      </PopoverContent>
    </Popover>
  );
};

export default OrganizationSwitcher;
